import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  message, 
  Tag, 
  Tooltip,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Typography,
  Progress,
  Popconfirm,
  Divider,
  Empty,
  Badge,
  notification,
  Switch,
  DatePicker
} from 'antd';
import { 
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  MoneyCollectOutlined,
  BarChartOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  ClearOutlined,
  WarningOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';

// 防抖函数
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 简化的样式定义
const styles = {
  container: {
    padding: 24
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  searchFilters: {
    marginBottom: 16,
    padding: 16,
    background: '#fafafa'
  }
};

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;

// 常量配置
const CONSTANTS = {
  TABS: {
    SERVICES: 'services',
    DOMAINS: 'domains',
    QUOTA: 'quota',
    STATISTICS: 'statistics',
    LOGS: 'logs'
  },
  ORDER_STATUS: {
    PENDING: 'pending',
    PAID: 'paid',
    REFUNDED: 'refunded',
    CLOSED: 'closed'
  },
  ORDER_STATUS_CONFIG: {
    pending: { color: 'processing', text: '待支付' },
    paid: { color: 'success', text: '已支付' },
    refunded: { color: 'warning', text: '已退款' },
    closed: { color: 'default', text: '已关闭' }
  },
  OPERATION_TYPES: {
    SERVICE_CREATED: 'service_created',
    SERVICE_ENABLED: 'service_enabled',
    SERVICE_DISABLED: 'service_disabled',
    SERVICE_DELETED: 'service_deleted',
    QUOTA_PURCHASED: 'quota_purchased',
    ADMIN_CONFIRM_PAYMENT: 'admin_confirm_payment',
    ADMIN_REFUND: 'admin_refund',
    ADMIN_CLOSE_ORDER: 'admin_close_order',
    // 管理员系统管理操作
    ADMIN_MANUAL_CHECK: 'admin_manual_check',
    ADMIN_RESET_REMINDERS: 'admin_reset_reminders',
    ADMIN_TOGGLE_REMINDER_SERVICE: 'admin_toggle_reminder_service'
  },
  OPERATION_TYPE_MAP: {
    // 🛡️ 服务相关操作 (Service Operations) - 实际使用
    service_created: { text: 'WAF服务创建', color: 'blue' },
    service_enabled: { text: 'WAF服务启用', color: 'green' },
    service_disabled: { text: 'WAF服务禁用', color: 'orange' },
    service_deleted: { text: 'WAF服务删除', color: 'red' },
    
    // 🌐 域名相关操作 (Domain Operations) - 实际使用
    domain_added: { text: '站点添加', color: 'blue' },
    domain_updated: { text: '站点配置更新', color: 'geekblue' },
    domain_deleted: { text: '站点删除', color: 'red' },
    domain_renewed: { text: '站点续费', color: 'gold' },
    
    // 🌐 域名相关操作 (Domain Operations) - 预留
    domain_enabled: { text: '站点启用', color: 'green' },
    domain_disabled: { text: '站点禁用', color: 'orange' },
    
    // 🔒 SSL证书相关操作 (SSL Certificate Operations) - 实际使用
    ssl_uploaded: { text: 'SSL证书上传', color: 'green' },
    ssl_deleted: { text: 'SSL证书删除', color: 'orange' },
    
    // 🔒 SSL证书相关操作 (SSL Certificate Operations) - 预留
    ssl_updated: { text: 'SSL证书更新', color: 'cyan' },
    
    // 💰 额度和支付相关操作 (Quota & Payment Operations) - 实际使用
    quota_purchased: { text: '额度购买', color: 'gold' },
    
    // 💰 额度和支付相关操作 (Quota & Payment Operations) - 预留
    quota_used: { text: '额度使用', color: 'volcano' },
    quota_expired: { text: '额度过期', color: 'red' },
    
    // 👨‍💼 管理员操作 (Administrator Operations) - 实际使用
    admin_update_domain: { text: '管理员修改站点', color: 'geekblue' },
    admin_confirm_payment: { text: '管理员确认支付', color: 'cyan' },
    admin_refund: { text: '管理员退款', color: 'purple' },
    admin_close_order: { text: '管理员关闭订单', color: 'magenta' },
    
    // 🔧 管理员系统管理操作 (Administrator System Management) - 新增
    admin_manual_check: { text: '手动触发WAF到期检查', color: 'lime' },
    admin_reset_reminders: { text: '重置到期提醒记录', color: 'orange' },
    admin_toggle_reminder_service: { text: '启停到期提醒服务', color: 'volcano' }
  },
  PAYMENT_METHODS: {
    alipay: '支付宝',
    admin_grant: '管理员赠送'
  },
  DEBOUNCE_DELAY: 500,
  EXPIRING_DAYS_WARNING: 30
};

const AdminWafManagement = () => {
  // 通用状态
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(CONSTANTS.TABS.SERVICES);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  // WAF服务管理状态
  const [services, setServices] = useState([]);
  const [servicesPagination, setServicesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [servicesFilters, setServicesFilters] = useState({
    search: '',
    status: undefined
  });
  const [serviceDetailVisible, setServiceDetailVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // 额度管理状态
  const [quotaModalVisible, setQuotaModalVisible] = useState(false);
  const [quotaForm] = Form.useForm();
  const [quotaRecords, setQuotaRecords] = useState([]);
  const [quotaRecordsPagination, setQuotaRecordsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 统计信息状态
  const [statistics, setStatistics] = useState({
    overview: {},
    sslStatistics: {},
    wafStatistics: {},
    servicesByLevel: [],
    monthlyServices: [],
    revenue: {},
    expiringSoon: []
  });

  // 操作日志状态
  const [operationLogs, setOperationLogs] = useState([]);
  const [operationLogsPagination, setOperationLogsPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [operationLogsFilters, setOperationLogsFilters] = useState({
    search: '',
    operationType: undefined,
    startDate: null,
    endDate: null
  });
  const [selectedLogRowKeys, setSelectedLogRowKeys] = useState([]);

  // 订单清理服务状态
  const [cleanupServiceStatus, setCleanupServiceStatus] = useState({
    isRunning: false,
    cleanupIntervalMinutes: 15,
    orderTimeoutHours: 1,
    deleteIntervalDays: 3,
    deleteDaysAfterClosed: 7,
    cleanupCronExpression: "*/15 * * * *",
    deleteCronExpression: "0 2 */3 * *",
    statistics: {
      totalPendingOrders: 0,
      expiredPendingOrders: 0,
      healthyPendingOrders: 0,
      totalClosedOrders: 0,
      expiredClosedOrders: 0,
      healthyClosedOrders: 0
    }
  });
  const [cleanupConfigVisible, setCleanupConfigVisible] = useState(false);
  const [cleanupConfigForm] = Form.useForm();

  // WAF到期提醒服务状态
  const [expiryReminderStatus, setExpiryReminderStatus] = useState({
    isRunning: false,
    lastCheckTime: null,
    pendingReminders: 0,
    checkInterval: '每15分钟检查一次',
    statistics: {
      totalActiveServices: 0,
      upcomingExpiryServices: 0,
      expiredServices: 0,
      healthyServices: 0
    }
  });
  const [upcomingExpiryServices, setUpcomingExpiryServices] = useState([]);
  const [expiryServicesVisible, setExpiryServicesVisible] = useState(false);

  // 域名管理状态
  const [domains, setDomains] = useState([]);
  const [domainsPagination, setDomainsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [domainsFilters, setDomainsFilters] = useState({
    search: '',
    status: undefined,
    hasSSL: undefined,
    expiringSoon: undefined
  });
  const [domainDetailVisible, setDomainDetailVisible] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainEditVisible, setDomainEditVisible] = useState(false);
  const [domainEditForm] = Form.useForm();
  const [sslDetailVisible, setSslDetailVisible] = useState(false);
  const [selectedSSL, setSelectedSSL] = useState(null);

  // 支付管理状态
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundForm] = Form.useForm();

  // 获取WAF服务列表（优化版）
  const fetchServices = useCallback(async (params = {}, showLoading = true) => {
    if (showLoading) setLoading(true);
    
    try {
      // 直接访问最新的状态值，避免依赖数组包含状态对象
      const currentPagination = servicesPagination;
      const currentFilters = servicesFilters;
      
      const requestParams = {
        page: params.page || currentPagination.current,
        limit: params.limit || currentPagination.pageSize,
        ...currentFilters,
        ...params
      };

      const response = await API.get('/api/admin/waf/services', { params: requestParams });

      if (response.data.code === 0) {
        const { services, pagination } = response.data.data;
        setServices(services);
        setServicesPagination(prev => ({
          ...prev,
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total
        }));
        setLastRefreshTime(new Date());
        
        // 显示成功提示（仅在手动刷新时）
        if (params.showSuccessMessage) {
          message.success(`已刷新，共加载 ${services.length} 条服务记录`);
        }
      } else {
        message.error(response.data.msg || '获取WAF服务列表失败');
      }
    } catch (error) {
      console.error('获取WAF服务列表失败:', error);
      
      if (error.response?.status === 403) {
        notification.error({
          message: '权限不足',
          description: '您没有权限访问WAF服务管理功能',
          duration: 5
        });
      } else if (error.response?.status >= 500) {
        notification.error({
          message: '服务器错误',
          description: '服务器暂时无法响应，请稍后重试',
          duration: 5
        });
      } else {
        message.error('获取WAF服务列表失败，请检查网络连接');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 防抖搜索
  const debouncedSearch = useMemo(
    () => debounce((searchParams) => {
      fetchServices({ ...searchParams, page: 1 });
    }, CONSTANTS.DEBOUNCE_DELAY),
    [fetchServices]
  );

  // 获取WAF服务详情（优化版）
  const fetchServiceDetail = useCallback(async (serviceId) => {
    if (!serviceId) {
      message.error('服务ID无效');
      return;
    }

    const loadingMsg = message.loading('加载服务详情中...', 0);
    
    try {
      const response = await API.get(`/api/admin/waf/services/${serviceId}`);
      if (response.data.code === 0) {
        setSelectedService(response.data.data);
        setServiceDetailVisible(true);
      } else {
        message.error(response.data.msg || '获取WAF服务详情失败');
      }
    } catch (error) {
      console.error('获取WAF服务详情失败:', error);
      message.error('获取WAF服务详情失败，请重试');
    } finally {
      loadingMsg();
    }
  }, []);

  // 切换WAF服务状态
  const toggleServiceStatus = useCallback(async (serviceId, currentStatus) => {
    try {
      const response = await API.put(`/api/admin/waf/services/${serviceId}/toggle`, {
        isEnabled: !currentStatus
      });

      if (response.data.code === 0) {
        message.success(response.data.msg);
        fetchServices();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('切换服务状态失败');
      console.error(error);
    }
  }, [fetchServices]);

  // 删除WAF服务
  const deleteService = useCallback(async (serviceId) => {
    try {
      const response = await API.delete(`/api/admin/waf/services/${serviceId}`);
      if (response.data.code === 0) {
        message.success(response.data.msg);
        fetchServices();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('删除WAF服务失败');
      console.error(error);
    }
  }, [fetchServices]);

  // 添加额度
  const handleAddQuota = async (values) => {
    try {
      const response = await API.post(`/api/admin/waf/services/${values.serviceId}/add-quota`, values);
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setQuotaModalVisible(false);
        quotaForm.resetFields();
        fetchServices();
        fetchQuotaRecords({ page: 1, limit: 10 });
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('添加额度失败');
      console.error(error);
    }
  };

  // 获取购买记录
  const fetchQuotaRecords = useCallback(async (customParams = {}) => {
    try {
      const requestParams = {
        page: 1,
        limit: 10,
        ...customParams
      };

      const response = await API.get('/api/admin/waf/quota-records', {
        params: requestParams
      });

      if (response.data.code === 0) {
        setQuotaRecords(response.data.data.records);
        setQuotaRecordsPagination({
          current: response.data.data.pagination.page,
          pageSize: response.data.data.pagination.limit,
          total: response.data.data.pagination.total
        });
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取购买记录失败');
      console.error(error);
    }
  }, []);

  // 获取统计信息
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/waf/statistics');
      if (response.data.code === 0) {
        setStatistics(response.data.data);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取统计信息失败');
      console.error(error);
    }
  }, []);

  // 获取订单清理服务状态
  const fetchCleanupServiceStatus = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/waf/cleanup/status');
      if (response.data.code === 0) {
        setCleanupServiceStatus(response.data.data);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取清理服务状态失败');
      console.error(error);
    }
  }, []);

  // 获取WAF到期提醒服务状态
  const fetchExpiryReminderStatus = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/waf/expiry-reminder/status');
      if (response.data.code === 0) {
        setExpiryReminderStatus(response.data.data);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取到期提醒服务状态失败');
      console.error(error);
    }
  }, []);

  // 获取即将到期的WAF服务列表
  const fetchUpcomingExpiryServices = useCallback(async (days = 7) => {
    try {
      const response = await API.get('/api/admin/waf/expiry-reminder/upcoming-expiry', {
        params: { days }
      });
      if (response.data.code === 0) {
        setUpcomingExpiryServices(response.data.data);
        setExpiryServicesVisible(true);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取即将到期WAF服务失败');
      console.error(error);
    }
  }, []);

  // 手动触发WAF到期检查
  const triggerManualExpiryCheck = async () => {
    Modal.confirm({
      title: '确认手动检查',
      content: (
        <div>
          <p>确定要手动触发WAF到期检查吗？</p>
          <div style={{ background: '#f6f8fa', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div>• 将检查所有启用的WAF服务的到期状态</div>
              <div>• 当前有 <Text strong style={{ color: '#f5222d' }}>{expiryReminderStatus.statistics.upcomingExpiryServices}</Text> 个服务即将到期</div>
              <div>• 当前有 <Text strong style={{ color: '#ff4d4f' }}>{expiryReminderStatus.statistics.expiredServices}</Text> 个服务已过期</div>
              <div>• 系统将自动发送相应的到期提醒通知</div>
            </Text>
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/waf/expiry-reminder/manual-check');
          if (response.data.code === 0) {
            message.success(response.data.msg);
            // 刷新状态
            fetchExpiryReminderStatus();
          } else {
            message.error(response.data.msg);
          }
        } catch (error) {
          message.error('手动检查失败');
          console.error(error);
        }
      }
    });
  };

  // 重置WAF到期提醒记录
  const resetExpiryReminders = async () => {
    Modal.confirm({
      title: '确认重置提醒记录',
      content: (
        <div>
          <p>确定要重置所有WAF到期提醒记录吗？</p>
          <div style={{ background: '#fff2f0', padding: '12px', borderRadius: '6px', marginTop: '12px', border: '1px solid #ffccc7' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div style={{ color: '#f5222d', fontWeight: 'bold', marginBottom: '8px' }}>⚠️ 操作说明</div>
              <div>• 将清除所有已发送的到期提醒记录</div>
              <div>• 系统将重新开始发送到期提醒通知</div>
              <div>• 用户可能会重复收到之前已发送过的提醒</div>
              <div>• 此操作不可逆，请谨慎操作</div>
            </Text>
          </div>
        </div>
      ),
      okText: '确认重置',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/waf/expiry-reminder/reset-reminders');
          if (response.data.code === 0) {
            message.success(response.data.msg);
            // 刷新状态
            fetchExpiryReminderStatus();
          } else {
            message.error(response.data.msg);
          }
        } catch (error) {
          message.error('重置提醒记录失败');
          console.error(error);
        }
      }
    });
  };

  // 切换WAF到期提醒服务状态
  const toggleExpiryReminderService = async (action) => {
    try {
      const response = await API.post('/api/admin/waf/expiry-reminder/toggle', { action });
      if (response.data.code === 0) {
        message.success(response.data.msg);
        // 刷新状态
        fetchExpiryReminderStatus();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error(`${action === 'start' ? '启动' : '停止'}服务失败`);
      console.error(error);
    }
  };

  // 手动触发订单清理
  const triggerManualCleanup = async () => {
    Modal.confirm({
      title: '确认手动清理',
      content: (
        <div>
          <p>确定要手动触发订单清理吗？</p>
          <div style={{ background: '#f6f8fa', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div>• 将清理所有超过 <Text strong>{cleanupServiceStatus.orderTimeoutHours}</Text> 小时的待支付订单</div>
              <div>• 当前有 <Text strong style={{ color: '#f5222d' }}>{cleanupServiceStatus.statistics.expiredPendingOrders}</Text> 个过期订单待清理</div>
              <div>• 清理后这些订单将被关闭并在 {cleanupServiceStatus.deleteDaysAfterClosed} 天后永久删除</div>
            </Text>
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/waf/cleanup/manual');
          if (response.data.code === 0) {
            message.success(response.data.msg);
            // 刷新状态
            fetchCleanupServiceStatus();
          } else {
            message.error(response.data.msg);
          }
        } catch (error) {
          message.error('手动清理失败');
          console.error(error);
        }
      }
    });
  };

  // 手动触发订单删除
  const triggerManualDelete = async () => {
    Modal.confirm({
      title: '确认手动删除',
      content: (
        <div>
          <p>确定要手动触发订单删除吗？</p>
          <div style={{ background: '#fff2f0', padding: '12px', borderRadius: '6px', marginTop: '12px', border: '1px solid #ffccc7' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div style={{ color: '#f5222d', fontWeight: 'bold', marginBottom: '8px' }}>⚠️ 危险操作警告</div>
              <div>• 将永久删除所有关闭超过 <Text strong>{cleanupServiceStatus.deleteDaysAfterClosed}</Text> 天的订单</div>
              <div>• 当前有 <Text strong style={{ color: '#fa8c16' }}>{cleanupServiceStatus.statistics.expiredClosedOrders}</Text> 个订单将被删除</div>
              <div>• 此操作不可逆，请谨慎操作</div>
            </Text>
          </div>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/waf/cleanup/manual-delete');
          if (response.data.code === 0) {
            message.success(response.data.msg);
            // 刷新状态
            fetchCleanupServiceStatus();
          } else {
            message.error(response.data.msg);
          }
        } catch (error) {
          message.error('手动删除失败');
          console.error(error);
        }
      }
    });
  };

  // 更新清理服务配置
  const updateCleanupConfig = async (values) => {
    try {
      // 只发送支持的配置参数
      const configParams = {};
      if (values.orderTimeoutHours) {
        configParams.orderTimeoutHours = values.orderTimeoutHours;
      }
      if (values.deleteDaysAfterClosed) {
        configParams.deleteDaysAfterClosed = values.deleteDaysAfterClosed;
      }

      const response = await API.put('/api/admin/waf/cleanup/config', configParams);
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setCleanupConfigVisible(false);
        cleanupConfigForm.resetFields();
        // 刷新状态
        fetchCleanupServiceStatus();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('更新配置失败');
      console.error(error);
    }
  };

  // 获取操作日志
  const fetchOperationLogs = useCallback(async (customParams = {}) => {
    try {
      // 直接访问最新的状态值，避免依赖数组包含状态对象
      const currentPagination = operationLogsPagination;
      const currentFilters = operationLogsFilters;
      
      const requestParams = {
        page: customParams.page || currentPagination.current,
        limit: customParams.limit || currentPagination.pageSize,
        ...currentFilters,
        ...customParams
      };

      // 处理搜索参数 - 后端支持IP地址和用户ID筛选
      if (requestParams.search) {
        // 将搜索关键词同时作为IP地址筛选，后端会自动匹配
        requestParams.ip = requestParams.search;
        // 移除通用搜索参数，使用具体的筛选参数
        delete requestParams.search;
      }

      // 移除后端不支持的参数
      delete requestParams.startDate;
      delete requestParams.endDate;
      delete requestParams.userRole; // 后端不支持userRole筛选

      const response = await API.get('/api/admin/waf/operation-logs', {
        params: requestParams
      });

      if (response.data.code === 0) {
        setOperationLogs(response.data.data.logs);
        setOperationLogsPagination({
          current: response.data.data.pagination.page,
          pageSize: response.data.data.pagination.limit,
          total: response.data.data.pagination.total
        });
        setLastRefreshTime(new Date());
        
        if (customParams.showSuccessMessage) {
          message.success(`已刷新，共加载 ${response.data.data.logs.length} 条操作日志`);
        }
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取操作日志失败');
      console.error(error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 防抖搜索 - 操作日志
  const debouncedLogSearch = useMemo(
    () => debounce((searchParams) => {
      fetchOperationLogs({ ...searchParams, page: 1 });
    }, CONSTANTS.DEBOUNCE_DELAY),
    [fetchOperationLogs]
  );

  // 获取域名列表
  const fetchDomains = useCallback(async (params = {}, showLoading = true) => {
    if (showLoading) setLoading(true);
    
    try {
      // 直接访问最新的状态值，避免依赖数组包含状态对象
      const currentPagination = domainsPagination;
      const currentFilters = domainsFilters;
      
      const requestParams = {
        page: params.page || currentPagination.current,
        limit: params.limit || currentPagination.pageSize,
        ...currentFilters,
        ...params
      };

      const response = await API.get('/api/admin/waf/domains', { params: requestParams });

      if (response.data.code === 0) {
        const { domains, pagination } = response.data.data;
        setDomains(domains);
        setDomainsPagination(prev => ({
          ...prev,
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total
        }));
        setLastRefreshTime(new Date());
        
        if (params.showSuccessMessage) {
          message.success(`已刷新，共加载 ${domains.length} 条域名记录`);
        }
      } else {
        message.error(response.data.msg || '获取域名列表失败');
      }
    } catch (error) {
      console.error('获取域名列表失败:', error);
      message.error('获取域名列表失败，请检查网络连接');
    } finally {
      if (showLoading) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 获取域名详情
  const fetchDomainDetail = useCallback(async (domainId, includeSSLContent = false) => {
    if (!domainId) {
      message.error('域名ID无效');
      return;
    }

    const loadingMsg = message.loading('加载域名详情中...', 0);
    
    try {
      const response = await API.get(`/api/admin/waf/domains/${domainId}`, {
        params: { includeSSLContent: includeSSLContent ? 'true' : 'false' }
      });
      if (response.data.code === 0) {
        setSelectedDomain(response.data.data);
        setDomainDetailVisible(true);
      } else {
        message.error(response.data.msg || '获取域名详情失败');
      }
    } catch (error) {
      console.error('获取域名详情失败:', error);
      message.error('获取域名详情失败，请重试');
    } finally {
      loadingMsg();
    }
  }, []);

  // 获取SSL证书详情
  const fetchSSLDetail = useCallback(async (domainId) => {
    if (!domainId) {
      message.error('域名ID无效');
      return;
    }

    const loadingMsg = message.loading('加载SSL证书详情中...', 0);
    
    try {
      const response = await API.get(`/api/admin/waf/domains/${domainId}/ssl`);
      if (response.data.code === 0) {
        setSelectedSSL(response.data.data);
        setSslDetailVisible(true);
      } else {
        message.error(response.data.msg || '获取SSL证书详情失败');
      }
    } catch (error) {
      console.error('获取SSL证书详情失败:', error);
      message.error('获取SSL证书详情失败，请重试');
    } finally {
      loadingMsg();
    }
  }, []);

  // 更新域名配置
  const updateDomainConfig = useCallback(async (values) => {
    try {
      // 构建配置对象，符合后端API期望的结构
      const updateData = {
        isEnabled: values.isEnabled,
        config: {
          protocol: values.protocol,
          originServer: values.originServer,
          originPort: values.originPort
        },
        expiredAt: values.expiredAt ? values.expiredAt.toISOString() : null,
        remark: values.remark
      };

      const response = await API.put(`/api/admin/waf/domains/${values.domainId}`, updateData);
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setDomainEditVisible(false);
        domainEditForm.resetFields();
        fetchDomains();
        if (domainDetailVisible) {
          fetchDomainDetail(values.domainId);
        }
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('更新域名配置失败');
      console.error(error);
    }
  }, [fetchDomains, domainDetailVisible, fetchDomainDetail, domainEditForm]);

  // 查询订单状态
  const queryOrderStatus = async (orderId) => {
    try {
      const response = await API.get(`/api/admin/waf/payment/query/${orderId}`);
      if (response.data.code === 0) {
        setSelectedOrder(response.data.data);
        setPaymentModalVisible(true);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('查询订单状态失败');
      console.error(error);
    }
  };

  // 确认订单支付
  const confirmPayment = async (orderId, reason) => {
    try {
      const response = await API.post(`/api/admin/waf/payment/confirm/${orderId}`, { reason });
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setPaymentModalVisible(false);
        fetchQuotaRecords({ page: 1, limit: 10 });
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('确认订单支付失败');
      console.error(error);
    }
  };

  // 申请退款
  const handleRefund = async (values) => {
    try {
      const response = await API.post(`/api/admin/waf/payment/refund/${values.orderId}`, values);
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setRefundModalVisible(false);
        refundForm.resetFields();
        fetchQuotaRecords({ page: 1, limit: 10 });
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('申请退款失败');
      console.error(error);
    }
  };

  // 关闭订单
  const closeOrder = async (orderId, reason) => {
    try {
      const response = await API.post(`/api/admin/waf/payment/close/${orderId}`, { reason });
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setPaymentModalVisible(false);
        fetchQuotaRecords({ page: 1, limit: 10 });
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('关闭订单失败');
      console.error(error);
    }
  };

  // 刷新当前标签页数据
  const refreshCurrentTab = useCallback(async (showMessage = true) => {
    setRefreshing(true);
    try {
      switch (activeTab) {
        case CONSTANTS.TABS.SERVICES:
          await fetchServices({ showSuccessMessage: showMessage });
          break;
        case CONSTANTS.TABS.DOMAINS:
          await fetchDomains({ showSuccessMessage: showMessage });
          break;
        case CONSTANTS.TABS.QUOTA:
          // 使用默认分页参数刷新，避免依赖状态
          await fetchQuotaRecords({ page: 1, limit: 10 });
          break;
        case CONSTANTS.TABS.STATISTICS:
          await fetchStatistics();
          await fetchCleanupServiceStatus();
          await fetchExpiryReminderStatus();
          break;
        case CONSTANTS.TABS.LOGS:
          await fetchOperationLogs({ showSuccessMessage: showMessage });
          break;
        default:
          break;
      }
    } finally {
      setRefreshing(false);
    }
  }, [activeTab, fetchServices, fetchDomains, fetchQuotaRecords, fetchStatistics, fetchOperationLogs, fetchCleanupServiceStatus, fetchExpiryReminderStatus]);

  // 清除筛选条件
  const clearFilters = useCallback(() => {
    if (activeTab === CONSTANTS.TABS.SERVICES) {
      setServicesFilters({
        search: '',
        status: undefined
      });
      fetchServices({ search: '', status: undefined, page: 1 });
    } else if (activeTab === CONSTANTS.TABS.DOMAINS) {
      setDomainsFilters({
        search: '',
        status: undefined,
        hasSSL: undefined,
        expiringSoon: undefined
      });
      fetchDomains({ search: '', status: undefined, hasSSL: undefined, expiringSoon: undefined, page: 1 });
    } else if (activeTab === CONSTANTS.TABS.LOGS) {
      setOperationLogsFilters({
        search: '',
        operationType: undefined,
        startDate: null,
        endDate: null
      });
      fetchOperationLogs({ 
        search: '', 
        operationType: undefined, 
        page: 1 
      });
    }
    message.success('已清除所有筛选条件');
  }, [activeTab, fetchServices, fetchDomains, fetchOperationLogs]);



  // 组件初始化
  useEffect(() => {
    refreshCurrentTab(false);
  }, [activeTab, refreshCurrentTab]);



  // WAF服务表格列定义（优化版）
  const serviceColumns = useMemo(() => [
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      fixed: 'left',
      render: (text) => <Text code>{text}</Text>,
      sorter: (a, b) => a.ip.localeCompare(b.ip)
    },
    {
      title: '用户信息',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
      render: (user) => user ? (
        <div>
          <div>{user.username}</div>
          <Text type="secondary">{user.email}</Text>
        </div>
      ) : <Text type="secondary">-</Text>,
      filters: [
        { text: '管理员', value: 'admin' },
        { text: '普通用户', value: 'user' }
      ],
      onFilter: (value, record) => record.userId?.role === value
    },

    {
      title: '额度使用',
      key: 'quota',
      width: 180,
      render: (_, record) => {
        const percent = record.totalQuota > 0 ? Math.round((record.usedQuota / record.totalQuota) * 100) : 0;
        const isOverLimit = record.usedQuota >= record.totalQuota;
        
        return (
          <div>
            <Progress 
              percent={percent}
              size="small"
              status={isOverLimit ? 'exception' : percent > 80 ? 'active' : 'normal'}
            />
            <div style={{ fontSize: '12px', marginTop: '2px' }}>
              <Text type={isOverLimit ? 'danger' : 'secondary'}>
                已用: {record.usedQuota}/{record.totalQuota}
                {isOverLimit && <WarningOutlined />}
              </Text>
              {record.basicQuota && record.extraQuota !== undefined && (
                <div style={{ fontSize: '11px', color: '#999' }}>
                  基础: {record.basicQuota} | 额外: {record.extraQuota || 0}
                </div>
              )}
            </div>
          </div>
        );
      },
      sorter: (a, b) => (a.usedQuota / a.totalQuota) - (b.usedQuota / b.totalQuota)
    },
    {
      title: '域名统计',
      dataIndex: 'stats',
      key: 'stats',
      width: 120,
      render: (stats) => stats ? (
        <div>
          <div>总计: <Text strong>{stats.totalDomains}</Text></div>
          <div>
            <Text type="success">启用: {stats.enabledDomains}</Text>
            {stats.disabledDomains > 0 && (
              <Text type="secondary"> 禁用: {stats.disabledDomains}</Text>
            )}
          </div>
        </div>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: '防护状态',
      key: 'protectionStatus',
      width: 120,
      render: (_, record) => {
        if (!record.hasBasicProtection) {
          return <Tag color="default">未购买</Tag>;
        }
        if (!record.isBasicProtectionActive) {
          return <Tag color="orange">已过期</Tag>;
        }
        if (record.isEnabled) {
          return <Tag color="success">防护中</Tag>;
        }
        return <Tag color="default">已暂停</Tag>;
      },
      filters: [
        { text: '防护中', value: 'active' },
        { text: '已暂停', value: 'paused' },
        { text: '已过期', value: 'expired' },
        { text: '未购买', value: 'unpurchased' }
      ],
      onFilter: (value, record) => {
        if (!record.hasBasicProtection) return value === 'unpurchased';
        if (!record.isBasicProtectionActive) return value === 'expired';
        if (record.isEnabled) return value === 'active';
        return value === 'paused';
      }
    },
    {
      title: '到期时间',
      key: 'expiredAt',
      width: 140,
      render: (_, record) => {
        const date = record.basicProtectionExpiredAt || record.expiredAt;
        if (!date) return <Text type="secondary">-</Text>;
        
        const isExpired = dayjs(date).isBefore(dayjs());
        const daysLeft = dayjs(date).diff(dayjs(), 'day');
        
        let textType = 'secondary';
        if (isExpired) {
          textType = 'danger';
        } else if (daysLeft <= 7) {
          textType = 'warning';
        }
        
        return (
          <div>
            <Text type={textType}>
              {dayjs(date).format('YYYY-MM-DD')}
            </Text>
            {!isExpired && daysLeft <= 30 && (
              <div style={{ fontSize: '11px', color: '#999' }}>
                {daysLeft}天后到期
              </div>
            )}
            {isExpired && <WarningOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />}
            {!isExpired && daysLeft <= 7 && <WarningOutlined style={{ marginLeft: 4, color: '#faad14' }} />}
          </div>
        );
      },
      sorter: (a, b) => {
        const dateA = a.basicProtectionExpiredAt || a.expiredAt;
        const dateB = b.basicProtectionExpiredAt || b.expiredAt;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dayjs(dateA).unix() - dayjs(dateB).unix();
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => fetchServiceDetail(record._id)}
            />
          </Tooltip>
          <Tooltip title={record.isEnabled ? '禁用服务' : '启用服务'}>
            <Button 
              type="link" 
              icon={record.isEnabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              size="small"
              onClick={() => toggleServiceStatus(record._id, record.isEnabled)}
            />
          </Tooltip>
          <Tooltip title="添加额度">
            <Button 
              type="link" 
              icon={<PlusOutlined />} 
              size="small"
              onClick={() => {
                quotaForm.setFieldsValue({ serviceId: record._id });
                setQuotaModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个WAF服务吗？"
            description="此操作将删除所有相关数据，不可恢复！"
            onConfirm={() => deleteService(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除服务">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ], [fetchServiceDetail, quotaForm, deleteService, toggleServiceStatus]);

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div>
          <Title level={2}>
            <DashboardOutlined />
            WAF服务管理
          </Title>
          {lastRefreshTime && (
            <Text type="secondary">
              最后更新: {dayjs(lastRefreshTime).format('HH:mm:ss')}
            </Text>
          )}
        </div>
        
        <div>
          <Space size="middle">
            {/* 清除筛选 */}
            {((activeTab === CONSTANTS.TABS.SERVICES && (servicesFilters.search || servicesFilters.status)) ||
              (activeTab === CONSTANTS.TABS.DOMAINS && (domainsFilters.search || domainsFilters.status || domainsFilters.hasSSL || domainsFilters.expiringSoon))) && (
              <Tooltip title="清除所有筛选条件">
                <Button 
                  icon={<ClearOutlined />}
                  onClick={clearFilters}
                  size="small"
                >
                  清除筛选
                </Button>
              </Tooltip>
            )}

            {/* 刷新按钮 */}
            <Button 
              type="primary" 
              icon={<ReloadOutlined spin={refreshing} />}
              loading={refreshing}
              onClick={() => refreshCurrentTab(true)}
            >
              刷新数据
            </Button>
          </Space>
        </div>
      </div>

      {/* 快速统计卡片 */}
      {activeTab === CONSTANTS.TABS.SERVICES && services.length > 0 && (
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="服务总数"
                value={services.length}
                prefix={<DashboardOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="启用服务"
                value={services.filter(s => s.isEnabled).length}
                prefix={<PlayCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="即将到期"
                value={services.filter(s => s.expiredAt && dayjs(s.expiredAt).diff(dayjs(), 'day') <= 7).length}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="额度告警"
                value={services.filter(s => s.usedQuota >= s.totalQuota).length}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 域名快速统计卡片 */}
      {activeTab === CONSTANTS.TABS.DOMAINS && domains.length > 0 && (
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="域名总数"
                value={domains.length}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已启用"
                value={domains.filter(d => d.isEnabled).length}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已配置SSL"
                value={domains.filter(d => d.ssl && d.ssl.hasCertificate).length}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="SSL即将过期"
                value={domains.filter(d => {
                  if (!d.ssl || !d.ssl.hasCertificate) return false;
                  const daysLeft = Math.ceil((new Date(d.ssl.certificateExpiredAt) - new Date()) / (1000 * 60 * 60 * 24));
                  return daysLeft <= 30 && daysLeft > 0;
                }).length}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <EditOutlined />
              WAF服务
            </span>
          } 
          key={CONSTANTS.TABS.SERVICES}
        >
          <Card>
            {/* 高级搜索区域 */}
            <div style={styles.searchFilters}>
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <Input
                    placeholder="搜索IP地址或用户名/邮箱"
                    prefix={<SearchOutlined />}
                    value={servicesFilters.search}
                    onChange={(e) => {
                      const value = e.target.value;
                      setServicesFilters(prev => ({ ...prev, search: value }));
                      // 防抖搜索
                      debouncedSearch({ search: value });
                    }}
                    allowClear
                    onClear={() => {
                      setServicesFilters(prev => ({ ...prev, search: '' }));
                      fetchServices({ search: '' });
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    placeholder="服务状态"
                    allowClear
                    value={servicesFilters.status}
                    onChange={(value) => {
                      setServicesFilters(prev => ({ ...prev, status: value }));
                      fetchServices({ status: value, page: 1 });
                    }}
                    suffixIcon={<FilterOutlined />}
                    style={{ width: '100%' }}
                  >
                    <Option value="enabled">
                      <Badge status="success" text="启用" />
                    </Option>
                    <Option value="disabled">
                      <Badge status="default" text="禁用" />
                    </Option>
                  </Select>
                </Col>

                <Col span={4}>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={() => fetchServices({ ...servicesFilters, page: 1 })}
                    block
                  >
                    搜索
                  </Button>
                </Col>
                <Col span={4}>
                  <Button 
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                    block
                    disabled={!servicesFilters.search && !servicesFilters.status}
                  >
                    重置
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              columns={serviceColumns}
              dataSource={services}
              rowKey="_id"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
                          {(servicesFilters.search || servicesFilters.status) ? 
                            '未找到符合条件的WAF服务' : 
                            '暂无WAF服务数据'
                          }
                        </div>
                        {(servicesFilters.search || servicesFilters.status) ? (
                          <Button type="primary" onClick={clearFilters} icon={<ClearOutlined />}>
                            清除筛选条件
                          </Button>
                        ) : (
                          <div style={{ color: '#999', fontSize: '14px' }}>
                            当系统中有WAF服务时，将在此处显示
                          </div>
                        )}
                      </div>
                    }
                  />
                )
              }}
              pagination={{
                ...servicesPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setServicesPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchServices({ page, limit: pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setServicesPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                  fetchServices({ page: 1, limit: size });
                }
              }}
              scroll={{ x: 1200 }}
              size="middle"
            />
          </Card>
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <FileTextOutlined />
              域名管理
            </span>
          } 
          key={CONSTANTS.TABS.DOMAINS}
        >
          <Card>
            {/* 域名搜索区域 */}
            <div style={styles.searchFilters}>
              <Row gutter={16} align="middle">
                <Col span={6}>
                  <Input
                    placeholder="搜索域名或IP地址"
                    prefix={<SearchOutlined />}
                    value={domainsFilters.search}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDomainsFilters(prev => ({ ...prev, search: value }));
                      // 防抖搜索
                      const debouncedDomainSearch = debounce((searchParams) => {
                        fetchDomains({ ...searchParams, page: 1 });
                      }, CONSTANTS.DEBOUNCE_DELAY);
                      debouncedDomainSearch({ search: value });
                    }}
                    allowClear
                    onClear={() => {
                      setDomainsFilters(prev => ({ ...prev, search: '' }));
                      fetchDomains({ search: '' });
                    }}
                  />
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="启用状态"
                    allowClear
                    value={domainsFilters.status}
                    onChange={(value) => {
                      setDomainsFilters(prev => ({ ...prev, status: value }));
                      fetchDomains({ status: value, page: 1 });
                    }}
                    style={{ width: '100%' }}
                  >
                    <Option value="enabled">
                      <Badge status="success" text="已启用" />
                    </Option>
                    <Option value="disabled">
                      <Badge status="default" text="已禁用" />
                    </Option>
                  </Select>
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="SSL状态"
                    allowClear
                    value={domainsFilters.hasSSL}
                    onChange={(value) => {
                      setDomainsFilters(prev => ({ ...prev, hasSSL: value }));
                      fetchDomains({ hasSSL: value, page: 1 });
                    }}
                    style={{ width: '100%' }}
                  >
                    <Option value="true">
                      <Badge status="success" text="已配置" />
                    </Option>
                    <Option value="false">
                      <Badge status="default" text="未配置" />
                    </Option>
                  </Select>
                </Col>
                <Col span={3}>
                  <Select
                    placeholder="到期状态"
                    allowClear
                    value={domainsFilters.expiringSoon}
                    onChange={(value) => {
                      setDomainsFilters(prev => ({ ...prev, expiringSoon: value }));
                      fetchDomains({ expiringSoon: value, page: 1 });
                    }}
                    style={{ width: '100%' }}
                  >
                    <Option value="true">
                      <Badge status="warning" text="即将到期" />
                    </Option>
                  </Select>
                </Col>
                <Col span={3}>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={() => fetchDomains({ ...domainsFilters, page: 1 })}
                    block
                  >
                    搜索
                  </Button>
                </Col>
                <Col span={3}>
                  <Button 
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                    block
                    disabled={!domainsFilters.search && !domainsFilters.status && !domainsFilters.hasSSL && !domainsFilters.expiringSoon}
                  >
                    重置
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              columns={[
                {
                  title: '域名',
                  dataIndex: 'domain',
                  key: 'domain',
                  width: 200,
                  fixed: 'left',
                  render: (text) => <Text code>{text}</Text>,
                  sorter: (a, b) => a.domain.localeCompare(b.domain)
                },
                {
                  title: 'WAF服务',
                  dataIndex: 'wafServiceId',
                  key: 'wafServiceId',
                  width: 150,
                  render: (wafService) => wafService ? (
                    <div>
                      <Text code>{wafService.ip}</Text>
                      {wafService.userId && (
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {wafService.userId.username}
                        </div>
                      )}
                    </div>
                  ) : <Text type="secondary">-</Text>
                },
                {
                  title: '配置信息',
                  key: 'config',
                  width: 180,
                  render: (_, record) => (
                    <div>
                      <div>协议: <Tag size="small">{record.config?.protocol || '-'}</Tag></div>
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>
                        源站: {record.config?.originServer || '-'}
                        {record.config?.originPort && `:${record.config.originPort}`}
                      </div>
                    </div>
                  )
                },
                {
                  title: 'SSL证书',
                  key: 'ssl',
                  width: 120,
                  render: (_, record) => {
                    if (!record.ssl || !record.ssl.hasCertificate) {
                      return <Tag color="default">未配置</Tag>;
                    }
                    
                    const now = new Date();
                    const expiredAt = new Date(record.ssl.certificateExpiredAt);
                    const daysLeft = Math.ceil((expiredAt - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft <= 0) {
                      return <Tag color="red">已过期</Tag>;
                    } else if (daysLeft <= 7) {
                      return <Tag color="orange">即将过期</Tag>;
                    } else if (daysLeft <= 30) {
                      return <Tag color="yellow">即将到期</Tag>;
                    } else {
                      return <Tag color="green">正常</Tag>;
                    }
                  },
                  filters: [
                    { text: '正常', value: 'valid' },
                    { text: '即将到期', value: 'expiring_soon' },
                    { text: '已过期', value: 'expired' },
                    { text: '未配置', value: 'none' }
                  ],
                  onFilter: (value, record) => {
                    if (!record.ssl || !record.ssl.hasCertificate) {
                      return value === 'none';
                    }
                    const now = new Date();
                    const expiredAt = new Date(record.ssl.certificateExpiredAt);
                    const daysLeft = Math.ceil((expiredAt - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft <= 0) return value === 'expired';
                    if (daysLeft <= 30) return value === 'expiring_soon';
                    return value === 'valid';
                  }
                },
                {
                  title: '域名状态',
                  key: 'status',
                  width: 100,
                  render: (_, record) => (
                    <Tag color={record.isEnabled ? 'green' : 'default'}>
                      {record.isEnabled ? '已启用' : '已禁用'}
                    </Tag>
                  ),
                  filters: [
                    { text: '已启用', value: true },
                    { text: '已禁用', value: false }
                  ],
                  onFilter: (value, record) => record.isEnabled === value
                },
                {
                  title: 'WAF保护',
                  key: 'wafStatus',
                  width: 120,
                  render: (_, record) => {
                    if (!record.wafServiceId || !record.wafServiceId.isEnabled) {
                      return <Tag color="default">服务禁用</Tag>;
                    }
                    
                    const date = record.expiredAt;
                    if (!date) return <Tag color="default">未设置</Tag>;
                    
                    const now = new Date();
                    const expiredAt = new Date(date);
                    const daysLeft = Math.ceil((expiredAt - now) / (1000 * 60 * 60 * 24));
                    
                    if (daysLeft <= 0) {
                      return <Tag color="red">已过期</Tag>;
                    } else if (daysLeft <= 7) {
                      return <Tag color="orange">即将到期</Tag>;
                    } else {
                      return <Tag color="green">防护中</Tag>;
                    }
                  }
                },
                {
                  title: '到期时间',
                  key: 'expiredAt',
                  width: 140,
                  render: (_, record) => {
                    const date = record.expiredAt;
                    if (!date) return <Text type="secondary">-</Text>;
                    
                    const isExpired = dayjs(date).isBefore(dayjs());
                    const daysLeft = dayjs(date).diff(dayjs(), 'day');
                    
                    let textType = 'secondary';
                    if (isExpired) {
                      textType = 'danger';
                    } else if (daysLeft <= 7) {
                      textType = 'warning';
                    }
                    
                    return (
                      <div>
                        <Text type={textType}>
                          {dayjs(date).format('YYYY-MM-DD')}
                        </Text>
                        {!isExpired && daysLeft <= 30 && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {daysLeft}天后到期
                          </div>
                        )}
                      </div>
                    );
                  },
                  sorter: (a, b) => {
                    if (!a.expiredAt && !b.expiredAt) return 0;
                    if (!a.expiredAt) return 1;
                    if (!b.expiredAt) return -1;
                    return dayjs(a.expiredAt).unix() - dayjs(b.expiredAt).unix();
                  }
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 200,
                  fixed: 'right',
                  render: (_, record) => (
                    <Space size="small">
                      <Tooltip title="查看详情">
                        <Button 
                          type="link" 
                          icon={<EyeOutlined />} 
                          size="small"
                          onClick={() => fetchDomainDetail(record._id)}
                        />
                      </Tooltip>
                      {record.ssl && record.ssl.hasCertificate && (
                        <Tooltip title="SSL证书详情">
                          <Button 
                            type="link" 
                            icon={<FileTextOutlined />} 
                            size="small"
                            onClick={() => fetchSSLDetail(record._id)}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title="编辑配置">
                        <Button 
                          type="link" 
                          icon={<EditOutlined />} 
                          size="small"
                          onClick={() => {
                            domainEditForm.setFieldsValue({
                              domainId: record._id,
                              domain: record.domain,
                              isEnabled: record.isEnabled,
                              protocol: record.config?.protocol,
                              originServer: record.config?.originServer,
                              originPort: record.config?.originPort,
                              expiredAt: record.expiredAt ? dayjs(record.expiredAt) : null
                            });
                            setDomainEditVisible(true);
                          }}
                        />
                      </Tooltip>
                    </Space>
                  )
                }
              ]}
              dataSource={domains}
              rowKey="_id"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
                          {(domainsFilters.search || domainsFilters.status || domainsFilters.hasSSL || domainsFilters.expiringSoon) ? 
                            '未找到符合条件的域名' : 
                            '暂无域名数据'
                          }
                        </div>
                        {(domainsFilters.search || domainsFilters.status || domainsFilters.hasSSL || domainsFilters.expiringSoon) ? (
                          <Button type="primary" onClick={clearFilters} icon={<ClearOutlined />}>
                            清除筛选条件
                          </Button>
                        ) : (
                          <div style={{ color: '#999', fontSize: '14px' }}>
                            当系统中有域名时，将在此处显示
                          </div>
                        )}
                      </div>
                    }
                  />
                )
              }}
              pagination={{
                ...domainsPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setDomainsPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchDomains({ page, limit: pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setDomainsPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                  fetchDomains({ page: 1, limit: size });
                }
              }}
              scroll={{ x: 1400 }}
              size="middle"
            />
          </Card>
        </TabPane>
        
        <TabPane 
          tab={<span><MoneyCollectOutlined />额度管理</span>} 
          key="quota"
        >
          <Card>
            <Table
              columns={[
                {
                  title: '订单号',
                  dataIndex: 'orderId',
                  key: 'orderId',
                  width: 200,
                  render: (text) => <Text code>{text}</Text>
                },
                {
                  title: 'WAF服务',
                  dataIndex: 'wafServiceId',
                  key: 'wafServiceId',
                  width: 150,
                  render: (service) => service ? (
                    <Text code>{service.ip}</Text>
                  ) : '-'
                },
                {
                  title: '用户信息',
                  dataIndex: 'userId',
                  key: 'userId',
                  width: 150,
                  render: (user) => user ? (
                    <div>
                      <div>{user.username}</div>
                      <Text type="secondary">{user.email}</Text>
                    </div>
                  ) : '-'
                },
                {
                  title: '额度数量',
                  dataIndex: 'quotaCount',
                  key: 'quotaCount',
                  width: 100,
                  render: (count) => <Text strong>{count}</Text>
                },
                {
                  title: '总金额',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  width: 100,
                  render: (amount) => <Text strong>¥{amount}</Text>
                },
                {
                  title: '支付方式',
                  dataIndex: 'paymentMethod',
                  key: 'paymentMethod',
                  width: 100,
                  render: (method) => {
                    const methods = {
                      'alipay': '支付宝',
                      'admin_grant': '管理员赠送'
                    };
                    return <span>{methods[method] || method}</span>;
                  }
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => {
                    const statusMap = {
                      'pending': { color: 'processing', text: '待支付' },
                      'paid': { color: 'success', text: '已支付' },
                      'refunded': { color: 'warning', text: '已退款' },
                      'closed': { color: 'default', text: '已关闭' }
                    };
                    const config = statusMap[status] || { color: 'default', text: status };
                    return <Tag color={config.color}>{config.text}</Tag>;
                  }
                },
                {
                  title: '创建时间',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 120,
                  render: (date) => dayjs(date).format('MM-DD HH:mm')
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 150,
                  render: (_, record) => (
                    <Space size="small">
                      <Tooltip title="查询订单状态">
                        <Button 
                          type="link" 
                          icon={<SearchOutlined />} 
                          size="small"
                          onClick={() => queryOrderStatus(record.orderId)}
                        />
                      </Tooltip>
                      {record.status === 'pending' && (
                        <Tooltip title="确认支付">
                          <Button 
                            type="link" 
                            icon={<CheckCircleOutlined />} 
                            size="small"
                            onClick={() => confirmPayment(record.orderId, '管理员手动确认')}
                          />
                        </Tooltip>
                      )}
                      {record.status === 'paid' && (
                        <Tooltip title="申请退款">
                          <Button 
                            type="link" 
                            icon={<MoneyCollectOutlined />} 
                            size="small"
                            onClick={() => {
                              refundForm.setFieldsValue({ 
                                orderId: record.orderId,
                                refundAmount: record.totalAmount
                              });
                              setRefundModalVisible(true);
                            }}
                          />
                        </Tooltip>
                      )}
                      {record.status === 'pending' && (
                        <Tooltip title="关闭订单">
                          <Button 
                            type="link" 
                            danger
                            icon={<CloseCircleOutlined />} 
                            size="small"
                            onClick={() => closeOrder(record.orderId, '管理员关闭订单')}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  )
                }
              ]}
              dataSource={quotaRecords}
              rowKey="_id"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
                          暂无额度购买记录
                        </div>
                        <div style={{ color: '#999', fontSize: '14px' }}>
                          用户购买额度后将在此处显示记录
                        </div>
                      </div>
                    }
                  />
                )
              }}
              pagination={{
                ...quotaRecordsPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setQuotaRecordsPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchQuotaRecords({ page, limit: pageSize });
                }
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </TabPane>

        <TabPane 
          tab={<span><BarChartOutlined />统计分析</span>} 
          key="statistics"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Card title="服务概览">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="总服务数" 
                      value={statistics.overview.totalServices || 0}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="启用服务" 
                      value={statistics.overview.enabledServices || 0}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="总域名数" 
                      value={statistics.overview.totalDomains || 0}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="总收入" 
                      value={statistics.revenue.totalRevenue || 0}
                      precision={2}
                      prefix="¥"
                    />
                  </Col>
                </Row>
              </Card>

              <Card title="SSL证书统计">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="已配置SSL" 
                      value={statistics.sslStatistics?.domainsWithSSL || 0}
                      suffix="个域名"
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="未配置SSL" 
                      value={statistics.sslStatistics?.domainsWithoutSSL || 0}
                      suffix="个域名"
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="即将过期" 
                      value={statistics.sslStatistics?.expiringSoonSSLCerts || 0}
                      suffix="个证书"
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="已过期" 
                      value={statistics.sslStatistics?.expiredSSLCerts || 0}
                      suffix="个证书"
                      valueStyle={{ color: '#ff4d4f' }}
                    />
                  </Col>
                </Row>
              </Card>

              <Card title="WAF保护统计">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic 
                      title="防护中域名" 
                      value={statistics.wafStatistics?.activeWAFDomains || 0}
                      suffix="个"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="即将到期" 
                      value={statistics.wafStatistics?.expiringSoonWAFDomains || 0}
                      suffix="个"
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="已过期" 
                      value={statistics.wafStatistics?.expiredWAFDomains || 0}
                      suffix="个"
                      valueStyle={{ color: '#ff4d4f' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {statistics.expiringSoon && statistics.expiringSoon.length > 0 && (
                         <Card title="即将到期服务">
              <Table
                dataSource={statistics.expiringSoon}
                rowKey="_id"
                                  pagination={false}
                  size="small"
                columns={[
                  {
                    title: 'IP地址',
                    dataIndex: 'ip',
                    key: 'ip',
                    render: (text) => <Text code>{text}</Text>
                  },
                  {
                    title: '用户',
                    dataIndex: 'userId',
                    key: 'userId',
                    render: (user) => user ? user.username : '-'
                  },
                  {
                    title: '到期时间',
                    dataIndex: 'expiredAt',
                    key: 'expiredAt',
                    render: (date) => (
                      <Text type="danger">
                        {dayjs(date).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    )
                  },
                  {
                    title: '剩余天数',
                    dataIndex: 'expiredAt',
                    key: 'remainingDays',
                    render: (date) => {
                      const days = dayjs(date).diff(dayjs(), 'day');
                      return (
                        <Text type={days <= 7 ? 'danger' : 'warning'}>
                          {days} 天
                        </Text>
                      );
                    }
                  }
                ]}
              />
            </Card>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Card title="收入统计">
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic 
                      title="总订单数" 
                      value={statistics.revenue.totalOrders || 0}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic 
                      title="平均订单金额" 
                      value={statistics.revenue.avgOrderValue || 0}
                      precision={2}
                      prefix="¥"
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={8}>
              <Card 
                title="WAF到期提醒服务"
                extra={
                  <Space>
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={triggerManualExpiryCheck}
                      loading={refreshing}
                    >
                      手动检查
                    </Button>
                    <Button 
                      size="small"
                      onClick={() => fetchUpcomingExpiryServices(7)}
                      loading={refreshing}
                    >
                      查看详情
                    </Button>
                    <Button 
                      size="small"
                      onClick={resetExpiryReminders}
                      loading={refreshing}
                    >
                      重置记录
                    </Button>
                    <Button 
                      size="small"
                      type={expiryReminderStatus.isRunning ? 'default' : 'primary'}
                      onClick={() => toggleExpiryReminderService(expiryReminderStatus.isRunning ? 'stop' : 'start')}
                      loading={refreshing}
                    >
                      {expiryReminderStatus.isRunning ? '停止' : '启动'}
                    </Button>
                  </Space>
                }
              >
                {/* 服务状态 */}
                <div style={{ marginBottom: 12 }}>
                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>服务状态</Text>
                      <div>
                        <Badge 
                          status={expiryReminderStatus.isRunning ? "processing" : "default"} 
                          text={expiryReminderStatus.isRunning ? "运行中" : "已停止"}
                        />
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>检查间隔</Text>
                      <div>
                        <Text strong style={{ fontSize: '13px' }}>
                          {expiryReminderStatus.checkInterval}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>上次检查</Text>
                      <div>
                        <Text style={{ fontSize: '13px' }}>
                          {expiryReminderStatus.lastCheckTime 
                            ? dayjs(expiryReminderStatus.lastCheckTime).format('MM-DD HH:mm')
                            : '-'
                          }
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>待发提醒</Text>
                      <div>
                        <Text style={{ fontSize: '13px' }}>
                          {expiryReminderStatus.pendingReminders || 0}条
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>
                
                <Divider style={{ margin: '12px 0' }} />
                
                {/* 服务统计 */}
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
                    服务状态统计
                  </Text>
                  <Row gutter={8}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                          {expiryReminderStatus.statistics.totalActiveServices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>总计</Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                          {expiryReminderStatus.statistics.upcomingExpiryServices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>即将到期</Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f5222d' }}>
                          {expiryReminderStatus.statistics.expiredServices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>已过期</Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                          {expiryReminderStatus.statistics.healthyServices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>正常</Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card 
                title="订单清理服务"
                extra={
                  <Space>
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={triggerManualCleanup}
                      loading={refreshing}
                    >
                      手动清理
                    </Button>
                    <Button 
                      danger
                      size="small"
                      onClick={triggerManualDelete}
                      loading={refreshing}
                      disabled={cleanupServiceStatus.statistics.expiredClosedOrders === 0}
                    >
                      手动删除
                    </Button>
                    <Button 
                      size="small"
                      onClick={() => {
                        cleanupConfigForm.setFieldsValue({
                          orderTimeoutHours: cleanupServiceStatus.orderTimeoutHours,
                          deleteDaysAfterClosed: cleanupServiceStatus.deleteDaysAfterClosed
                        });
                        setCleanupConfigVisible(true);
                      }}
                    >
                      配置
                    </Button>
                  </Space>
                }
              >
                {/* 服务状态 */}
                <div style={{ marginBottom: 12 }}>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>服务状态</Text>
                      <div>
                        <Badge 
                          status={cleanupServiceStatus.isRunning ? "processing" : "default"} 
                          text={cleanupServiceStatus.isRunning ? "运行中" : "已停止"}
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>清理间隔</Text>
                      <div>
                        <Text strong style={{ fontSize: '13px' }}>
                          {cleanupServiceStatus.cleanupIntervalMinutes}分钟
                        </Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>订单超时</Text>
                      <div>
                        <Text strong style={{ fontSize: '13px' }}>
                          {cleanupServiceStatus.orderTimeoutHours}小时
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>删除间隔</Text>
                      <div>
                        <Text style={{ fontSize: '13px' }}>
                          每{cleanupServiceStatus.deleteIntervalDays}天
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>删除延迟</Text>
                      <div>
                        <Text style={{ fontSize: '13px' }}>
                          关闭后{cleanupServiceStatus.deleteDaysAfterClosed}天
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>
                
                <Divider style={{ margin: '12px 0' }} />
                
                {/* 待支付订单统计 */}
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
                    待支付订单
                  </Text>
                  <Row gutter={8}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                          {cleanupServiceStatus.statistics.totalPendingOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>总计</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f5222d' }}>
                          {cleanupServiceStatus.statistics.expiredPendingOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>已过期</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                          {cleanupServiceStatus.statistics.healthyPendingOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>正常</Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* 已关闭订单统计 */}
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
                    已关闭订单
                  </Text>
                  <Row gutter={8}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                          {cleanupServiceStatus.statistics.totalClosedOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>总计</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fa8c16' }}>
                          {cleanupServiceStatus.statistics.expiredClosedOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>待删除</Text>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                          {cleanupServiceStatus.statistics.healthyClosedOrders}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>正常</Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane 
          tab={<span><FileTextOutlined />操作日志</span>} 
          key="logs"
        >
          <Card>
            {/* 筛选器 */}
            <div style={styles.searchFilters}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Input
                    placeholder="搜索用户名、IP或描述"
                    prefix={<SearchOutlined />}
                    value={operationLogsFilters.search}
                                         onChange={(e) => {
                       const value = e.target.value;
                       setOperationLogsFilters(prev => ({ ...prev, search: value }));
                       debouncedLogSearch({ search: value });
                     }}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="操作类型"
                    value={operationLogsFilters.operationType}
                    onChange={(value) => {
                      setOperationLogsFilters(prev => ({ ...prev, operationType: value }));
                      fetchOperationLogs({ operationType: value, page: 1 });
                    }}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {Object.entries(CONSTANTS.OPERATION_TYPE_MAP).map(([key, config]) => (
                      <Option key={key} value={key}>
                        <Tag color={config.color} style={{ marginRight: 8 }}>
                          {config.text}
                        </Tag>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      loading={refreshing}
                      onClick={() => refreshCurrentTab(true)}
                    >
                      刷新
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={() => {
                        setOperationLogsFilters({
                          search: '',
                          operationType: undefined,
                          startDate: null,
                          endDate: null
                        });
                        fetchOperationLogs({ 
                          search: '', 
                          operationType: undefined, 
                          page: 1 
                        });
                        message.success('已清除所有筛选条件');
                      }}
                    >
                      清除筛选
                    </Button>
                  </Space>
                </Col>
              </Row>
              
              {/* 暂时移除日期筛选功能，后端暂不支持
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12}>
                  <Space>
                    <span style={{ color: '#666' }}>时间范围：</span>
                    <DatePicker
                      placeholder="开始时间"
                      value={operationLogsFilters.startDate}
                      onChange={(date) => {
                        setOperationLogsFilters(prev => ({ ...prev, startDate: date }));
                        fetchOperationLogs({ 
                          startDate: date, 
                          endDate: operationLogsFilters.endDate, 
                          page: 1 
                        });
                      }}
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                    />
                    <DatePicker
                      placeholder="结束时间"
                      value={operationLogsFilters.endDate}
                      onChange={(date) => {
                        setOperationLogsFilters(prev => ({ ...prev, endDate: date }));
                        fetchOperationLogs({ 
                          startDate: operationLogsFilters.startDate, 
                          endDate: date, 
                          page: 1 
                        });
                      }}
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                    />
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  {lastRefreshTime && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      最后更新: {dayjs(lastRefreshTime).format('MM-DD HH:mm:ss')}
                    </Text>
                  )}
                </Col>
              </Row>
              */}
              
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12}>
                  {lastRefreshTime && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      最后更新: {dayjs(lastRefreshTime).format('MM-DD HH:mm:ss')}
                    </Text>
                  )}
                </Col>
              </Row>
            </div>

            <Table
              columns={[
                {
                  title: '时间',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 140,
                  fixed: 'left',
                  sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
                  render: (date) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {dayjs(date).format('MM-DD')}
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(date).format('HH:mm:ss')}
                      </Text>
                    </div>
                  )
                },
                {
                  title: '操作用户',
                  dataIndex: 'userId',
                  key: 'userId',
                  width: 140,
                  render: (user) => user ? (
                    <div>
                      <div style={{ fontWeight: 500 }}>{user.username}</div>
                      <div>
                        <Tag 
                          size="small" 
                          color={user.role === 'admin' ? 'red' : 'blue'}
                        >
                          {user.role === 'admin' ? '管理员' : '用户'}
                        </Tag>
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">系统</Text>
                  )
                },
                {
                  title: 'IP地址',
                  dataIndex: 'ip',
                  key: 'ip',
                  width: 140,
                  render: (text) => (
                    <Text code style={{ fontSize: '12px' }}>
                      {text}
                    </Text>
                  )
                },
                {
                  title: '操作类型',
                  dataIndex: 'operationType',
                  key: 'operationType',
                  width: 160,
                  filters: Object.entries(CONSTANTS.OPERATION_TYPE_MAP).map(([key, config]) => ({
                    text: config.text,
                    value: key
                  })),
                  onFilter: (value, record) => record.operationType === value,
                  render: (type) => {
                    const config = CONSTANTS.OPERATION_TYPE_MAP[type] || { text: type, color: 'default' };
                    return (
                      <Tag color={config.color} style={{ margin: 0 }}>
                        {config.text}
                      </Tag>
                    );
                  }
                },
                {
                  title: '操作描述',
                  dataIndex: 'operationDescription',
                  key: 'operationDescription',
                  ellipsis: {
                    showTitle: false
                  },
                  render: (text) => (
                    <Tooltip title={text}>
                      <div style={{ 
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {text}
                      </div>
                    </Tooltip>
                  )
                },
                {
                  title: '详情',
                  dataIndex: 'details',
                  key: 'details',
                  width: 100,
                  fixed: 'right',
                  render: (details, record) => (
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={() => {
                        Modal.info({
                          title: '操作详情',
                          width: 600,
                          content: (
                            <div>
                              <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label="操作时间">
                                  {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                                </Descriptions.Item>
                                <Descriptions.Item label="操作用户">
                                  {record.userId ? `${record.userId.username} (${record.userId.role})` : '系统'}
                                </Descriptions.Item>
                                <Descriptions.Item label="IP地址">
                                  {record.ip}
                                </Descriptions.Item>
                                <Descriptions.Item label="操作类型">
                                  <Tag color={CONSTANTS.OPERATION_TYPE_MAP[record.operationType]?.color || 'default'}>
                                    {CONSTANTS.OPERATION_TYPE_MAP[record.operationType]?.text || record.operationType}
                                  </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="操作描述">
                                  {record.operationDescription}
                                </Descriptions.Item>
                                {details && (
                                  <Descriptions.Item label="详细信息">
                                    <pre style={{ 
                                      background: '#f5f5f5', 
                                      padding: '8px', 
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      maxHeight: '200px',
                                      overflow: 'auto'
                                    }}>
                                      {JSON.stringify(details, null, 2)}
                                    </pre>
                                  </Descriptions.Item>
                                )}
                              </Descriptions>
                            </div>
                          )
                        });
                      }}
                    >
                      查看
                    </Button>
                  )
                }
              ]}
              dataSource={operationLogs}
              rowKey="_id"
              loading={loading}
              size="middle"
              rowSelection={{
                selectedRowKeys: selectedLogRowKeys,
                onChange: setSelectedLogRowKeys,
                selections: [
                  Table.SELECTION_ALL,
                  Table.SELECTION_INVERT,
                  Table.SELECTION_NONE
                ]
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
                          暂无操作日志记录
                        </div>
                        <div style={{ color: '#999', fontSize: '14px' }}>
                          系统操作日志将在此处显示
                        </div>
                      </div>
                    }
                  />
                )
              }}
              pagination={{
                ...operationLogsPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setOperationLogsPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchOperationLogs({ page, limit: pageSize });
                }
              }}
              scroll={{ x: 1200 }}
            />
            
            {/* 批量操作工具栏 */}
            {selectedLogRowKeys.length > 0 && (
              <div style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                background: '#fff',
                padding: '12px 16px',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000
              }}>
                <Space>
                  <Text>已选择 {selectedLogRowKeys.length} 条记录</Text>
                  <Button size="small" onClick={() => setSelectedLogRowKeys([])}>
                    取消选择
                  </Button>
                  <Button 
                    size="small" 
                    type="primary"
                    onClick={() => {
                      const selectedLogs = operationLogs.filter(log => 
                        selectedLogRowKeys.includes(log._id)
                      );
                      
                      Modal.info({
                        title: '批量导出操作日志',
                        width: 800,
                        content: (
                          <div>
                            <Text>将导出以下 {selectedLogs.length} 条操作日志：</Text>
                            <pre style={{ 
                              marginTop: 16,
                              background: '#f5f5f5', 
                              padding: '12px',
                              borderRadius: '4px',
                              maxHeight: '400px',
                              overflow: 'auto'
                            }}>
                              {selectedLogs.map(log => 
                                `${dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss')} | ${log.userId?.username || '系统'} | ${log.operationType} | ${log.operationDescription}`
                              ).join('\n')}
                            </pre>
                          </div>
                        )
                      });
                    }}
                  >
                    导出选中
                  </Button>
                </Space>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* 添加额度模态框 */}
      <Modal
        title="添加额度"
        open={quotaModalVisible}
        onCancel={() => {
          setQuotaModalVisible(false);
          quotaForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={quotaForm}
          layout="vertical"
          onFinish={handleAddQuota}
        >
          <Form.Item name="serviceId" hidden>
            <Input />
          </Form.Item>
          
          <Form.Item
            label="额度数量"
            name="quotaCount"
            rules={[
              { required: true, message: '请输入额度数量' },
              { type: 'number', min: 1, message: '额度数量必须大于0' }
            ]}
          >
                            <InputNumber min={1} placeholder="请输入额度数量" />
          </Form.Item>

          <Form.Item
            label="有效期（月）"
            name="duration"
            initialValue={1}
            rules={[
              { required: true, message: '请输入有效期' },
              { type: 'number', min: 1, message: '有效期必须大于0' }
            ]}
          >
                            <InputNumber min={1} max={12} placeholder="请输入有效期" />
          </Form.Item>

          <Form.Item
            label="添加原因"
            name="reason"
          >
            <Input.TextArea rows={3} placeholder="请输入添加额度的原因（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加额度
              </Button>
              <Button onClick={() => {
                setQuotaModalVisible(false);
                quotaForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 服务详情模态框 */}
      <Modal
        title="WAF服务详情"
        open={serviceDetailVisible}
        onCancel={() => setServiceDetailVisible(false)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="IP地址">
                <Text code>{selectedService.service.ip}</Text>
              </Descriptions.Item>

              <Descriptions.Item label="用户信息">
                {selectedService.service.userId ? (
                  <div>
                    <div>{selectedService.service.userId.username}</div>
                    <Text type="secondary">{selectedService.service.userId.email}</Text>
                  </div>
                ) : '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label="防护状态">
                {!selectedService.service.hasBasicProtection ? (
                  <Tag color="default">未购买基础防护</Tag>
                ) : !selectedService.service.isBasicProtectionActive ? (
                  <Tag color="orange">基础防护已过期</Tag>
                ) : selectedService.service.isEnabled ? (
                  <Tag color="success">防护中</Tag>
                ) : (
                  <Tag color="default">已暂停</Tag>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="额度详情">
                <div>
                  <div>已用/总计: {selectedService.service.usedQuota}/{selectedService.service.totalQuota}</div>
                  {selectedService.service.basicQuota && selectedService.service.extraQuota !== undefined && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      基础额度: {selectedService.service.basicQuota} | 额外额度: {selectedService.service.extraQuota || 0}
                    </Text>
                  )}
                </div>
              </Descriptions.Item>
              
              <Descriptions.Item label="基础防护到期">
                {selectedService.service.basicProtectionExpiredAt ? 
                  dayjs(selectedService.service.basicProtectionExpiredAt).format('YYYY-MM-DD HH:mm') : '-'
                }
              </Descriptions.Item>
              
              <Descriptions.Item label="服务到期时间">
                {selectedService.service.expiredAt ? 
                  dayjs(selectedService.service.expiredAt).format('YYYY-MM-DD HH:mm') : '-'
                }
              </Descriptions.Item>
            </Descriptions>

            <Divider>域名列表</Divider>
            <Table
              dataSource={selectedService.domains}
              rowKey="_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '域名',
                  dataIndex: 'domain',
                  key: 'domain'
                },
                {
                  title: '状态',
                  dataIndex: 'isEnabled',
                  key: 'isEnabled',
                  render: (enabled) => (
                    <Tag color={enabled ? 'success' : 'default'}>
                      {enabled ? '启用' : '禁用'}
                    </Tag>
                  )
                },
                {
                  title: '创建时间',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (date) => dayjs(date).format('MM-DD HH:mm')
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 订单详情模态框 */}
      <Modal
        title="订单详情"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="订单号">
                <Text code>{selectedOrder.localOrder.orderId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedOrder.localOrder.status === 'paid' ? 'success' : 'processing'}>
                  {selectedOrder.localOrder.status === 'paid' ? '已支付' : '待支付'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="金额">
                ¥{selectedOrder.localOrder.totalAmount}
              </Descriptions.Item>
              <Descriptions.Item label="额度数量">
                {selectedOrder.localOrder.quotaCount}
              </Descriptions.Item>
              <Descriptions.Item label="支付方式">
                {selectedOrder.localOrder.paymentMethod === 'alipay' ? '支付宝' : '管理员赠送'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(selectedOrder.localOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              {selectedOrder.localOrder.remark && (
                <Descriptions.Item label="备注">
                  {selectedOrder.localOrder.remark}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedOrder.alipayStatus && (
              <div>
                <Title level={5}>支付宝状态</Title>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="交易状态">
                    {selectedOrder.alipayStatus.tradeStatus}
                  </Descriptions.Item>
                  {selectedOrder.alipayStatus.tradeNo && (
                    <Descriptions.Item label="支付宝订单号">
                      <Text code>{selectedOrder.alipayStatus.tradeNo}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedOrder.alipayStatus.totalAmount && (
                    <Descriptions.Item label="支付金额">
                      ¥{selectedOrder.alipayStatus.totalAmount}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {selectedOrder.alipayStatus?.error && (
              <div>
                <Title level={5}>查询错误</Title>
                <Text type="danger">{selectedOrder.alipayStatus.error}</Text>
              </div>
            )}

            <div>
              <Space>
                {selectedOrder.localOrder.status === 'pending' && (
                  <>
                    <Button 
                      type="primary"
                      onClick={() => confirmPayment(selectedOrder.localOrder.orderId, '管理员手动确认')}
                    >
                      确认支付
                    </Button>
                    <Button 
                      danger
                      onClick={() => closeOrder(selectedOrder.localOrder.orderId, '管理员关闭订单')}
                    >
                      关闭订单
                    </Button>
                  </>
                )}
                <Button onClick={() => setPaymentModalVisible(false)}>
                  关闭
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* 退款模态框 */}
      <Modal
        title="申请退款"
        open={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          refundForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={refundForm}
          layout="vertical"
          onFinish={handleRefund}
        >
          <Form.Item name="orderId" hidden>
            <Input />
          </Form.Item>
          
          <Form.Item
            label="退款金额"
            name="refundAmount"
            rules={[
              { required: true, message: '请输入退款金额' },
              { type: 'number', min: 0.01, message: '退款金额必须大于0' }
            ]}
          >
            <InputNumber 
              min={0.01} 
              precision={2}
              placeholder="请输入退款金额"
              addonBefore="¥"
            />
          </Form.Item>

          <Form.Item
            label="退款原因"
            name="refundReason"
            rules={[{ required: true, message: '请输入退款原因' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入退款原因" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                申请退款
              </Button>
              <Button onClick={() => {
                setRefundModalVisible(false);
                refundForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 域名详情模态框 */}
      <Modal
        title="域名详情"
        open={domainDetailVisible}
        onCancel={() => setDomainDetailVisible(false)}
        footer={null}
        width={800}
      >
        {selectedDomain && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="域名" span={2}>
                <Text code>{selectedDomain.domain.domain}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="WAF服务IP">
                <Text code>{selectedDomain.domain.wafServiceId?.ip || '-'}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="用户信息">
                {selectedDomain.domain.wafServiceId?.userId ? (
                  <div>
                    <div>{selectedDomain.domain.wafServiceId.userId.username}</div>
                    <Text type="secondary">{selectedDomain.domain.wafServiceId.userId.email}</Text>
                  </div>
                ) : '-'}
              </Descriptions.Item>
              
              <Descriptions.Item label="协议">
                <Tag>{selectedDomain.domain.config?.protocol || '-'}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="源站地址">
                {selectedDomain.domain.config?.originServer || '-'}
                {selectedDomain.domain.config?.originPort && `:${selectedDomain.domain.config.originPort}`}
              </Descriptions.Item>
              
              <Descriptions.Item label="域名状态">
                <Tag color={selectedDomain.domain.isEnabled ? 'green' : 'default'}>
                  {selectedDomain.domain.isEnabled ? '已启用' : '已禁用'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="SSL证书">
                {selectedDomain.domain.ssl?.hasCertificate ? (
                  <div>
                    <Tag color="green">已配置</Tag>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      到期时间: {dayjs(selectedDomain.domain.ssl.certificateExpiredAt).format('YYYY-MM-DD')}
                    </div>
                  </div>
                ) : (
                  <Tag color="default">未配置</Tag>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="WAF状态">
                {selectedDomain.domain.wafStatus === 'active' ? (
                  <Tag color="green">防护中</Tag>
                ) : selectedDomain.domain.wafStatus === 'expiring_soon' ? (
                  <Tag color="orange">即将到期</Tag>
                ) : selectedDomain.domain.wafStatus === 'expired' ? (
                  <Tag color="red">已过期</Tag>
                ) : (
                  <Tag color="default">未设置</Tag>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="到期时间">
                {selectedDomain.domain.expiredAt ? 
                  dayjs(selectedDomain.domain.expiredAt).format('YYYY-MM-DD HH:mm') : '-'
                }
              </Descriptions.Item>
              
              <Descriptions.Item label="创建时间" span={2}>
                {dayjs(selectedDomain.domain.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {selectedDomain.recentLogs && selectedDomain.recentLogs.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <Divider>最近操作记录</Divider>
                <Table
                  dataSource={selectedDomain.recentLogs}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '操作类型',
                      dataIndex: 'operationType',
                      key: 'operationType',
                      render: (type) => {
                        const typeConfig = CONSTANTS.OPERATION_TYPE_MAP[type];
                        return typeConfig ? (
                          <Tag color={typeConfig.color}>{typeConfig.text}</Tag>
                        ) : type;
                      }
                    },
                    {
                      title: '操作描述',
                      dataIndex: 'operationDescription',
                      key: 'operationDescription'
                    },
                    {
                      title: '操作人',
                      dataIndex: 'userId',
                      key: 'userId',
                      render: (user) => user ? user.username : '-'
                    },
                    {
                      title: '操作时间',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      render: (date) => dayjs(date).format('MM-DD HH:mm')
                    }
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* SSL证书详情模态框 */}
      <Modal
        title="SSL证书详情"
        open={sslDetailVisible}
        onCancel={() => setSslDetailVisible(false)}
        footer={null}
        width={900}
      >
        {selectedSSL && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="域名" span={2}>
                <Text code>{selectedSSL.domain}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="WAF服务IP">
                <Text code>{selectedSSL.ip}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="证书状态">
                <Tag color={
                  selectedSSL.status === 'valid' ? 'green' : 
                  selectedSSL.status === 'expiring_soon' ? 'orange' : 'red'
                }>
                  {selectedSSL.status === 'valid' ? '正常' : 
                   selectedSSL.status === 'expiring_soon' ? '即将到期' : '已过期'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="到期时间">
                {dayjs(selectedSSL.certificateExpiredAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              
              <Descriptions.Item label="剩余天数">
                <Text type={selectedSSL.daysUntilExpiry <= 0 ? 'danger' : 
                          selectedSSL.daysUntilExpiry <= 30 ? 'warning' : 'secondary'}>
                  {selectedSSL.daysUntilExpiry <= 0 ? '已过期' : `${selectedSSL.daysUntilExpiry}天`}
                </Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="上传时间" span={2}>
                {dayjs(selectedSSL.uploadedAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {selectedSSL.certificateInfo && (
              <div style={{ marginTop: '20px' }}>
                <Divider>证书信息</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="证书格式">
                    {selectedSSL.certificateInfo.format}
                  </Descriptions.Item>
                  <Descriptions.Item label="文件大小">
                    {selectedSSL.certificateInfo.size} 字符
                  </Descriptions.Item>
                  <Descriptions.Item label="行数">
                    {selectedSSL.certificateInfo.lines}
                  </Descriptions.Item>
                  <Descriptions.Item label="私钥">
                    {selectedSSL.certificateInfo.hasPrivateKey ? '已配置' : '未配置'}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <Divider>证书内容</Divider>
              <Input.TextArea
                value={selectedSSL.certificateContent}
                rows={10}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            {selectedSSL.privateKeyContent && (
              <div style={{ marginTop: '20px' }}>
                <Divider>私钥内容</Divider>
                <Input.TextArea
                  value={selectedSSL.privateKeyContent}
                  rows={8}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 域名编辑模态框 */}
      <Modal
        title="编辑域名配置"
        open={domainEditVisible}
        onCancel={() => {
          setDomainEditVisible(false);
          domainEditForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={domainEditForm}
          layout="vertical"
          onFinish={updateDomainConfig}
        >
          <Form.Item name="domainId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label="域名"
            name="domain"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="启用状态"
            name="isEnabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="协议"
            name="protocol"
          >
            <Select placeholder="请选择协议">
              <Option value="http">HTTP</Option>
              <Option value="https">HTTPS</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                label="源站地址"
                name="originServer"
                rules={[{ required: true, message: '请输入源站地址' }]}
              >
                <Input placeholder="请输入源站地址" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="源站端口"
                name="originPort"
                rules={[
                  { required: true, message: '请输入源站端口' },
                  { type: 'number', min: 1, max: 65535, message: '端口范围1-65535' }
                ]}
              >
                <InputNumber 
                  min={1} 
                  max={65535} 
                  placeholder="端口"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="到期时间"
            name="expiredAt"
            rules={[{ required: true, message: '请设置到期时间' }]}
          >
            <DatePicker 
              showTime 
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              placeholder="请选择到期时间"
            />
          </Form.Item>

          <Form.Item
            label="备注"
            name="remark"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="请输入操作备注（可选）"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存配置
              </Button>
              <Button onClick={() => {
                setDomainEditVisible(false);
                domainEditForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 订单清理配置模态框 */}
      <Modal
        title="配置订单清理服务"
        open={cleanupConfigVisible}
        onCancel={() => {
          setCleanupConfigVisible(false);
          cleanupConfigForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 20 }}>
          <Text strong>当前服务配置</Text>
          <Descriptions 
            bordered 
            size="small" 
            column={2} 
            style={{ marginTop: 12 }}
          >
            <Descriptions.Item label="服务状态">
              <Badge 
                status={cleanupServiceStatus.isRunning ? "processing" : "default"} 
                text={cleanupServiceStatus.isRunning ? "运行中" : "已停止"}
              />
            </Descriptions.Item>
            <Descriptions.Item label="清理间隔">
              {cleanupServiceStatus.cleanupIntervalMinutes}分钟
            </Descriptions.Item>
            <Descriptions.Item label="删除间隔">
              每{cleanupServiceStatus.deleteIntervalDays}天
            </Descriptions.Item>
            <Descriptions.Item label="删除延迟">
              关闭后{cleanupServiceStatus.deleteDaysAfterClosed}天
            </Descriptions.Item>
            <Descriptions.Item label="清理定时任务" span={2}>
              <Text code style={{ fontSize: '12px' }}>
                {cleanupServiceStatus.cleanupCronExpression}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="删除定时任务" span={2}>
              <Text code style={{ fontSize: '12px' }}>
                {cleanupServiceStatus.deleteCronExpression}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Divider />

        <Form
          form={cleanupConfigForm}
          layout="vertical"
          onFinish={updateCleanupConfig}
        >
          <Text strong>可调整配置</Text>
          
          <Form.Item
            label="订单超时时间（小时）"
            name="orderTimeoutHours"
            rules={[
              { required: true, message: '请输入超时时间' },
              { type: 'number', min: 1, max: 168, message: '超时时间必须在1-168小时之间' }
            ]}
            help="系统将自动清理超过指定时间的待支付订单"
            style={{ marginTop: 16 }}
          >
            <InputNumber 
              min={1} 
              max={168}
              placeholder="请输入超时时间"
              style={{ width: '100%' }}
              addonAfter="小时"
            />
          </Form.Item>

          <Form.Item
            label="订单删除延迟时间（天）"
            name="deleteDaysAfterClosed"
            rules={[
              { required: true, message: '请输入删除延迟时间' },
              { type: 'number', min: 1, max: 365, message: '删除延迟时间必须在1-365天之间' }
            ]}
            help="关闭的订单将在指定天数后被永久删除"
          >
            <InputNumber 
              min={1} 
              max={365}
              placeholder="请输入删除延迟时间"
              style={{ width: '100%' }}
              addonAfter="天"
            />
          </Form.Item>

          <div style={{ 
            background: '#f6f8fa', 
            padding: '12px', 
            borderRadius: '6px',
            marginBottom: '16px' 
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div>• 超时时间建议设置1-24小时，当前设置为 {cleanupServiceStatus.orderTimeoutHours} 小时</div>
              <div>• 删除延迟建议设置3-30天，当前设置为 {cleanupServiceStatus.deleteDaysAfterClosed} 天</div>
              <div>• 清理操作每 {cleanupServiceStatus.cleanupIntervalMinutes} 分钟自动执行一次</div>
              <div>• 删除操作每 {cleanupServiceStatus.deleteIntervalDays} 天自动执行一次</div>
              <div>• 配置更新后立即生效</div>
            </Text>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存配置
              </Button>
              <Button onClick={() => {
                setCleanupConfigVisible(false);
                cleanupConfigForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* WAF到期服务详情模态框 */}
      <Modal
        title="WAF服务到期详情"
        open={expiryServicesVisible}
        onCancel={() => setExpiryServicesVisible(false)}
        footer={null}
        width={1200}
      >
        {upcomingExpiryServices.upcomingExpiry && (
          <div>
            {/* 统计摘要 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic
                    title="即将到期"
                    value={upcomingExpiryServices.summary?.totalUpcoming || 0}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="已过期"
                    value={upcomingExpiryServices.summary?.totalExpired || 0}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="高优先级"
                    value={upcomingExpiryServices.summary?.highPriority || 0}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="中优先级"
                    value={upcomingExpiryServices.summary?.mediumPriority || 0}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="低优先级"
                    value={upcomingExpiryServices.summary?.lowPriority || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={4}>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={() => fetchUpcomingExpiryServices(7)}
                    loading={refreshing}
                    block
                  >
                    刷新数据
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* 即将到期的服务 */}
            {upcomingExpiryServices.upcomingExpiry?.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 16 }}>
                  即将到期的服务 ({upcomingExpiryServices.upcomingExpiry.length})
                </Title>
                <Table
                  dataSource={upcomingExpiryServices.upcomingExpiry}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'IP地址',
                      dataIndex: 'ip',
                      key: 'ip',
                      render: (text) => <Text code>{text}</Text>
                    },
                    {
                      title: '用户信息',
                      dataIndex: 'userId',
                      key: 'userId',
                      render: (user) => user ? (
                        <div>
                          <div>{user.username}</div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
                        </div>
                      ) : '-'
                    },
                    {
                      title: '到期时间',
                      dataIndex: 'basicProtectionExpiredAt',
                      key: 'basicProtectionExpiredAt',
                      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
                    },
                    {
                      title: '剩余天数',
                      dataIndex: 'daysUntilExpiry',
                      key: 'daysUntilExpiry',
                      render: (days) => (
                        <Text type={days <= 1 ? 'danger' : days <= 3 ? 'warning' : 'secondary'}>
                          {days}天
                        </Text>
                      )
                    },
                    {
                      title: '优先级',
                      dataIndex: 'priority',
                      key: 'priority',
                      render: (priority) => {
                        const config = {
                          high: { color: 'red', text: '高' },
                          medium: { color: 'orange', text: '中' },
                          low: { color: 'blue', text: '低' }
                        };
                        const p = config[priority] || { color: 'default', text: priority };
                        return <Tag color={p.color}>{p.text}</Tag>;
                      }
                    },
                    {
                      title: '额度使用',
                      key: 'quota',
                      render: (_, record) => (
                        <Text>
                          {record.usedQuota}/{record.totalQuota}
                        </Text>
                      )
                    }
                  ]}
                />
              </div>
            )}

            {/* 已过期的服务 */}
            {upcomingExpiryServices.expired?.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 16 }}>
                  已过期的服务 ({upcomingExpiryServices.expired.length})
                </Title>
                <Table
                  dataSource={upcomingExpiryServices.expired}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'IP地址',
                      dataIndex: 'ip',
                      key: 'ip',
                      render: (text) => <Text code>{text}</Text>
                    },
                    {
                      title: '用户信息',
                      dataIndex: 'userId',
                      key: 'userId',
                      render: (user) => user ? (
                        <div>
                          <div>{user.username}</div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
                        </div>
                      ) : '-'
                    },
                    {
                      title: '过期时间',
                      dataIndex: 'basicProtectionExpiredAt',
                      key: 'basicProtectionExpiredAt',
                      render: (date) => (
                        <Text type="danger">
                          {dayjs(date).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      )
                    },
                    {
                      title: '过期天数',
                      dataIndex: 'daysOverdue',
                      key: 'daysOverdue',
                      render: (days) => (
                        <Text type="danger">
                          已过期 {days}天
                        </Text>
                      )
                    },
                    {
                      title: '状态',
                      key: 'status',
                      render: () => <Tag color="red">紧急</Tag>
                    },
                    {
                      title: '额度使用',
                      key: 'quota',
                      render: (_, record) => (
                        <Text>
                          {record.usedQuota}/{record.totalQuota}
                        </Text>
                      )
                    }
                  ]}
                />
              </div>
            )}

            {(!upcomingExpiryServices.upcomingExpiry?.length && !upcomingExpiryServices.expired?.length) && (
              <Empty
                description="暂无即将到期或已过期的WAF服务"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminWafManagement; 